/**
 * Shared careers listing layout (filters + table) used by full careers page,
 * Contentful JobListing block, and /jobadder dev preview route.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import {useJobs} from '@amblique/king-living/app/hooks/use-jobs';
import {getStickyChromeBottomPx} from '@amblique/king-living/app/utils/utils';

import {
  Box,
  Button,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Grid,
  GridItem,
  Heading,
  Text,
  useDisclosure,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {formatJobLocation} from './format-job-location';
import JobFilter from './job-filter';
import JobListing from './job-listing';

const JOBS_PER_PAGE = 20;

const careersJobBoardMessages = defineMessages({
  title: {
    id: 'careers_job_board.heading.opportunities',
    defaultMessage: 'Current Opportunities',
  },
  showingCount: {
    id: 'careers_job_board.subheading.showing_count',
    defaultMessage: 'Showing {shown} of {total} positions',
  },
  filteredNote: {
    id: 'careers_job_board.subheading.filtered_note',
    defaultMessage: ' (filtered)',
  },
  errorFetchFallback: {
    id: 'careers_job_board.error.fetch_failed',
    defaultMessage: 'Failed to fetch jobs',
  },
  errorLoadFallback: {
    id: 'careers_job_board.error.load_failed',
    defaultMessage: 'Failed to load job listings',
  },
  filterFlyoutTitle: {
    id: 'job_filter.flyout.title',
    defaultMessage: 'Filters',
  },
  filterByButton: {
    id: 'job_filter.button.filter_by',
    defaultMessage: 'Filter by',
  },
});

/** @param {{showEdgePadding?: boolean}} props */
const CareersJobBoard = ({showEdgePadding = true}) => {
  const intl = useIntl();
  const {data, isLoading, error: jobsQueryError, refetch} = useJobs();
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const boardTitleAreaRef = useRef(null);

  const jobs = data?.jobs ?? [];

  const error = useMemo(() => {
    if (!jobsQueryError) return null;

    if (jobsQueryError.code === 'FETCH_FAILED') {
      return intl.formatMessage(careersJobBoardMessages.errorFetchFallback);
    }

    return (
      jobsQueryError.message ||
      intl.formatMessage(careersJobBoardMessages.errorLoadFallback)
    );
  }, [intl, jobsQueryError]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const filteredJobs = useMemo(() => {
    if (!jobs || jobs.length === 0) return [];

    return jobs.filter((job) => {
      if (filters.departments && filters.departments.length > 0) {
        const jobDept = job.category || job.department || '';
        if (!filters.departments.some((d) => jobDept.toLowerCase() === d.toLowerCase())) {
          return false;
        }
      }

      if (filters.locations && filters.locations.length > 0) {
        const jobLocation = formatJobLocation(job.location);

        if (!jobLocation || !filters.locations.some((l) => jobLocation.toLowerCase() === l.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [jobs, filters]);

  const paginatedJobs = useMemo(() => {
    const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
    const endIndex = startIndex + JOBS_PER_PAGE;
    return filteredJobs.slice(startIndex, endIndex);
  }, [filteredJobs, currentPage]);

  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);

  const scrollToOpportunitiesSection = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // After drawer close: align section top to site chrome bottom only.
    // Do not stack boardTitleArea `scroll-margin-top` — that double-counts and stops above Clear All.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const titleEl = boardTitleAreaRef.current;

        if (!titleEl) {
          return;
        }

        const rect = titleEl.getBoundingClientRect();
        const scrollY = window.scrollY;
        const headerHeightPx = getStickyChromeBottomPx();
        const targetTop = Math.max(0, rect.top + scrollY - headerHeightPx);

        window.scrollTo({top: targetTop, behavior: 'auto'});
      });
    });
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    scrollToOpportunitiesSection();
  };

  const boardTheme = useMultiStyleConfig('CareersJobBoard', {});
  const hasActiveFilters =
    (filters.departments?.length ?? 0) > 0 || (filters.locations?.length ?? 0) > 0;
  const shownCount =
    filteredJobs.length > 0 ?
      Math.min(currentPage * JOBS_PER_PAGE, filteredJobs.length) :
      0;
  const filterDrawer = useDisclosure();

  const handleFilterDrawerCloseComplete = () => {
    scrollToOpportunitiesSection();
  };

  return (
    <Container {...boardTheme.boardContainer}>
      <Box display={{base: 'block', md: 'none'}} {...boardTheme.filterMobileBar}>
        <VStack spacing={4} align="stretch">
          <Button {...boardTheme.filterMobileTrigger} onClick={filterDrawer.onOpen}>
            <FormattedMessage {...careersJobBoardMessages.filterByButton} />
          </Button>
          <JobFilter filters={filters} jobs={jobs} onFilterChange={setFilters} variant="chipsOnly" />
        </VStack>
      </Box>

      <Grid {...boardTheme.boardGrid}>
        <GridItem display={{base: 'none', md: 'block'}}>
          <Box {...boardTheme.filterStickyOuter}>
            <JobFilter filters={filters} jobs={jobs} onFilterChange={setFilters} variant="full" />
          </Box>
        </GridItem>

        <GridItem>
          <Box ref={boardTitleAreaRef} {...boardTheme.boardTitleArea}>
            <Heading {...boardTheme.boardHeading}>
              <FormattedMessage {...careersJobBoardMessages.title} />
            </Heading>
          </Box>

          <JobListing
            currentPage={currentPage}
            error={error}
            isLoading={isLoading}
            jobs={paginatedJobs}
            onJobsRetry={refetch}
            onPageChange={handlePageChange}
            resultSummary={
              filteredJobs.length > 0 ?
                (
                  <Text {...boardTheme.listingResultSummary}>
                    <FormattedMessage
                      {...careersJobBoardMessages.showingCount}
                      values={{shown: shownCount, total: filteredJobs.length}}
                    />
                    {hasActiveFilters && (
                      <FormattedMessage {...careersJobBoardMessages.filteredNote} />
                    )}
                  </Text>
                ) :
                null
            }
            totalJobs={filteredJobs.length}
            totalPages={totalPages}
          />
        </GridItem>
      </Grid>

      <Drawer
        blockScrollOnMount={false}
        isOpen={filterDrawer.isOpen}
        placement="right"
        size="full"
        onClose={filterDrawer.onClose}
        onCloseComplete={handleFilterDrawerCloseComplete}
      >
        <DrawerOverlay bg="blackAlpha.400" />
        <DrawerContent {...boardTheme.filterFlyoutContent}>
          <DrawerHeader {...boardTheme.filterFlyoutHeader}>
            <DrawerCloseButton
              sx={{
                top: '17px',
                right: '17px',
                boxShadow: 'none',
                _focus: {boxShadow: 'none'},
              }}
              position="absolute"
            />
            <Text {...boardTheme.filterFlyoutTitle}>
              <FormattedMessage {...careersJobBoardMessages.filterFlyoutTitle} />
            </Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <JobFilter
              drawerIsOpen={filterDrawer.isOpen}
              filters={filters}
              jobs={jobs}
              onFilterChange={setFilters}
              variant="flyout"
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Container>
  );
};

export default CareersJobBoard;
